import { z } from 'zod';
import { NormPoint } from './primitives.ts';
import { CrossingDirection, VehicleClass } from './round.ts';

/** Locked per round (its canonical hash goes into the manifest as configHash). Normalised to the frame. */
export const VisionCalibration = z.object({
  calibrationVersion: z.literal(1),
  roi: z.array(NormPoint).min(3).max(32),
  /** `curve`: point the line passes through at its middle (curved line); absent = straight segment. */
  line: z.object({ lineId: z.string().min(1).max(32), a: NormPoint, b: NormPoint, countedDirection: CrossingDirection, curve: NormPoint.optional(), zone: z.object({ a: NormPoint, b: NormPoint }).optional() }),
  includedClasses: z.array(VehicleClass).min(1),
  minConfidence: z.number().min(0.05).max(0.95),
  /** Dead band around the line, fraction of frame height. */
  hysteresis: z.number().min(0).max(0.1),
  minDisplacement: z.number().min(0).max(0.2),
  tracking: z.object({
    highThresh: z.number().min(0.05).max(0.99),
    lowThresh: z.number().min(0.01).max(0.9),
    minHits: z.number().int().min(1).max(10),
    maxAgeFrames: z.number().int().min(1).max(120),
  }),
  stride: z.number().int().min(1).max(6),
  /** LINE_OCCUPANCY: one crossing per continuous occupancy of the line (long vessels). Default TRACK_CROSSING. */
  countingMode: z.enum(['TRACK_CROSSING', 'LINE_OCCUPANCY']).optional(),
  /**
   * Towed units: a second crossing in the same direction and lane (position along the line within
   * `maxLaneFrac`) within `maxGapMs` of a counted one is the trailer of that vehicle, counted once.
   * Absent = off (calibrations before the line placement stage).
   */
  towMerge: z.object({ maxGapMs: z.number().int().min(100).max(1500), maxLaneFrac: z.number().min(0.02).max(0.5) }).optional(),
  /**
   * ROI_V1 (absent): crossings count only inside the ROI, after two observations beyond the dead band.
   * SEGMENT_V2: every crossing of the drawn segment counts (no ROI clipping); one observation beyond two
   * dead bands decides it (vehicles lost right after the line).
   * SEGMENT_V3: SEGMENT_V2, and a new track starting where another just stopped being seen (occlusion, fast
   * vehicle) continues that track's side of the line, so an ID switch at the line does not lose the vehicle.
   */
  crossingRule: z.enum(['ROI_V1', 'SEGMENT_V2', 'SEGMENT_V3']).optional(),
});
export type VisionCalibration = z.infer<typeof VisionCalibration>;

/** Browser/engine → vision worker. The worker resolves sourceId to media itself; clients never send paths. */
export const VisionClientMessage = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('start'),
    sourceId: z.string().min(1).max(64),
    sourceEpoch: z.string().min(1).max(160),
    startPtsMicros: z.number().int().nonnegative(),
    calibration: VisionCalibration,
    configHash: z.string().regex(/^[0-9a-f]{64}$/),
  }),
  /** Latest presented frame; the worker never runs more than a small lead ahead of it. */
  z.object({ type: z.literal('presented'), sourceEpoch: z.string(), ptsMicros: z.number().int().nonnegative() }),
  z.object({ type: z.literal('stop') }),
]);
export type VisionClientMessage = z.infer<typeof VisionClientMessage>;

export const VisionTrack = z.object({
  id: z.number().int(),
  box: z.tuple([z.number(), z.number(), z.number(), z.number()]),
  cls: z.string(),
  confirmed: z.boolean(),
});

/** Raw crossing from the worker, before it is attributed to a round. */
export const VisionCrossing = z.object({
  type: z.literal('crossing'),
  eventId: z.string().min(1).max(64),
  sourceEpoch: z.string(),
  trackId: z.string(),
  lineId: z.string(),
  direction: CrossingDirection,
  vehicleClass: z.string(),
  confidence: z.number().min(0).max(1),
  ptsMicros: z.number().int().nonnegative(),
  uncertain: z.boolean(),
  uncertainty: z.array(z.string()),
  /** Classes seen on ≥ 20 % of the track's frames: what CLASS_AMBIGUOUS is ambiguous between. */
  classCandidates: z.array(z.string()).optional(),
  modelVersion: z.string(),
  configHash: z.string(),
});
export type VisionCrossing = z.infer<typeof VisionCrossing>;

export const VisionAlarmKind = z.enum(['FRAME_GAP', 'PTS_NOT_INCREASING', 'REPEATED_FRAME', 'CAMERA_MOVED']);

export const VisionServerMessage = z.discriminatedUnion('type', [
  z.object({ type: z.literal('ready'), sourceEpoch: z.string(), modelVersion: z.string(), modelSha256: z.string().nullable(), width: z.number().int(), height: z.number().int(), durationMicros: z.number().int().nullable().optional() }),
  z.object({ type: z.literal('frame'), sourceEpoch: z.string(), pts: z.number().int(), tracks: z.array(VisionTrack) }),
  VisionCrossing,
  /** `expected`: a documented property of this source (e.g. archival film), recorded but not escalated. */
  z.object({ type: z.literal('alarm'), sourceEpoch: z.string(), kind: VisionAlarmKind, pts: z.number().int().optional(), fromPts: z.number().int().optional(), toPts: z.number().int().optional(), expected: z.boolean().optional() }),
  z.object({ type: z.literal('progress'), sourceEpoch: z.string(), processedPts: z.number().int(), lagMicros: z.number().int().nullable() }),
  z.object({ type: z.literal('ended'), sourceEpoch: z.string() }),
  z.object({ type: z.literal('error'), code: z.string(), message: z.string() }),
]);
export type VisionServerMessage = z.infer<typeof VisionServerMessage>;
